import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _templateObject, _templateObject2, _templateObject3;
import React from 'react';
import _ from 'lodash';
import { Input, Header, Message, Button, Form, Segment, Divider, Checkbox, Grid, Modal } from 'semantic-ui-react';
import { withFormik } from 'formik';
import gql from 'graphql-tag';
import Cookies from 'universal-cookie';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import { AuthClient, EnjoyIcon } from '@emyw/core-lib';
import SocialLoginForm from '../SocialLoginForm';
import { normalizeErrors, logOut } from '../utils';
import ButtonLink from '../ButtonLink';
import ChooseProfileForm from '../ChooseProfileForm';
import EnterPinForm from '../EnterPinForm';
import UpdatePinForm from '../UpdatePinForm';
import '../primitives.css';
const {
  REACT_APP_ENJOY_SITE_ROOT_URL
} = process.env;
// const { REACT_APP_TOKEN_NAME, REACT_APP_REFRESH_TOKEN_NAME } = process.env;

const handleLoginSuccess = (user, profile, _ref) => {
  let {
    token,
    refreshToken,
    onLogin,
    onClose,
    rememberMe,
    selector,
    validator
  } = _ref;
  log.trace('handleLoginSuccess');
  // we should probably need these so we can make the query later to set the active profile
  // localStorage.setItem(REACT_APP_TOKEN_NAME, token);
  // localStorage.setItem(REACT_APP_REFRESH_TOKEN_NAME, refreshToken);
  if (rememberMe) {
    // put the selector and validator in a cookie
    const cookies = new Cookies();
    cookies.set('rem', JSON.stringify({
      selector,
      validator
    }), {
      path: '/',
      sameSite: 'lax'
    });
  }
  log.trace('logged in', user, profile);
  if (onLogin) onLogin(user, profile, token, refreshToken); // expect the host of this control to implement this
  else log.warn('onLogin prop required but not provided');
  // Doing 'setSubmitting' may cause 'Can only update a mounted or mounting component.' exception.
  // We shouldn't need it since onLogin is required and will likely redirect the user to another page.
  // setSubmitting(false);
  if (onClose) onClose(); // the case when we are hosted from a dialog
};
const handleServerLoginResponse = _ref2 => {
  let {
    loginData,
    rememberMe,
    onLogin,
    onClose,
    setSubmitting,
    setErrors,
    setValues,
    values,
    fromSocialLogin
  } = _ref2;
  log.trace('handleServerLoginResponse');
  const {
    ok,
    errors,
    token,
    refreshToken,
    selector,
    validator,
    activeProfileId,
    userId,
    email,
    Profiles
  } = loginData;
  // log.trace(loginData);
  if (!activeProfileId) {
    // log.trace('need to select a profile');
    // onSelectProfile(Profiles);
    setSubmitting(true);
    setValues({
      ...values,
      NoActiveProfile: {
        id: userId,
        email,
        Profiles,
        loginData: {
          token,
          refreshToken,
          onLogin,
          onClose,
          rememberMe,
          selector,
          validator
        }
      }
    });
    return;
  }
  const profile = Profiles.find(entry => entry.id === activeProfileId);
  if (!profile) throw new Error('Expected profile not found', activeProfileId, Profiles);
  if (fromSocialLogin && profile.securedByPIN) {
    // if the social profile is securedByPin, then prompt for the pin after successful login
    setSubmitting(true);
    setValues({
      ...values,
      ForceProfilePinPrompt: {
        user: {
          id: userId
        },
        profile,
        loginData: {
          token,
          refreshToken,
          onLogin,
          onClose,
          rememberMe,
          selector,
          validator
        }
      }
    });
  } else if (ok) {
    handleLoginSuccess({
      id: userId
    }, {
      id: activeProfileId
    }, {
      token,
      refreshToken,
      onLogin,
      onClose,
      rememberMe,
      selector,
      validator
    });
  } else {
    setSubmitting(false);
    setErrors(normalizeErrors(errors));
  }
};
const LoginLogic = withFormik({
  validateOnBlur: false,
  validateOnChange: false,
  // Transform outer props into form values
  mapPropsToValues: () => ({
    email: '',
    password: '',
    rememberMe: false,
    chooseFromProfiles: null,
    ForceProfilePinPrompt: null,
    forgotPinModalOpen: false
  }),
  // Add a custom validation function (this can be async too!)
  validate: values => {
    // , props
    const errors = {};
    // Email
    if (!values.email) {
      errors.email = 'Email required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    // Password
    if (!values.password) {
      errors.password = 'Password required';
    } else if (values.password.length < 5) {
      errors.password = 'Password must be at least 5 characters';
    }
    return errors;
  },
  // Submission handler
  handleSubmit: async (values, _ref3) => {
    let {
      props: {
        onClose,
        onLogin,
        loginMutation
      },
      setSubmitting,
      setErrors,
      setValues
    } = _ref3;
    try {
      // log.trace(values);
      const response = await loginMutation({
        variables: {
          email: values.email,
          password: values.password,
          rememberMe: values.rememberMe
        }
      });
      if (response.data && response.data.login.ok) {
        handleServerLoginResponse({
          loginData: response.data.login,
          rememberMe: values.rememberMe,
          onLogin,
          onClose,
          setSubmitting,
          setErrors,
          setValues,
          values
        });
      } else {
        if (response.networkError) logOut(); // clear out potentially bad tokens
        const errorMessage = response.networkError || ((((response.data || {}).login || {}).errors || [])[0] || '').message;
        log.trace(errorMessage);
        throw new Error("Login Error: ".concat(JSON.stringify(errorMessage)));
      }
    } catch (err) {
      setSubmitting(false);
      setErrors({
        login: [err.message]
      });
    }
  }
});

/** Login Form is intended as a general purpose login page or dialog that may be used by multiple clients
 * (i.e. web app, website, browser extension, etc..)
 * <br /><br />
 * <hr />
 * <br />
 * <div>
 * <h4>Back-end Requirements</h4>
 * A backend graphql api endpoint, <a rel=noopener href="https://github.com/emyw/auth-api">auth-api</a>,
 * is needed for this login form to function.
 * <h4>Local Login</h4>
 * This form includes a local account login feature with links to:
 * <ul>
 * <li><a href=".#NewAccountForm">New Account Creation</a></li>
 * <li><a href=".#ForgotPasswordForm">Forgot Password</a></li>
 * </ul>
 * <h4>Tokens</h4>
 * Once successfully logged in, a token and refershToken from the server are added to localStorage and is sent back in headers
 * to the server with each subsequent request for validation.<br />
 * <h4>Social Login</h4>
 * Included are social login buttons for facebook and google which obtain an access token.<br />
 * Refer to component: <a href=".#SocialLoginForm">Forgot Password</a>
 * <h4>Remember Me</h4>
 * Remember Me uses the selector/validator pattern and stores a cookie which may be used to auto-authenticate a user between sessions.<br />
 * <h4>Unverified Account Login Attempt</h4>
 * If the user attempts to login with an account that has not yet had its email verified, it will present a validation message
 * to the user and offer them a link to resend a new verification link.
 * </div>
 */
const LoginForm = _ref4 => {
  let {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setValues,
    setFieldValue,
    isSubmitting,
    touched,
    errors,
    onClose,
    onLogin,
    onForgotPassword,
    onNewAccount,
    resendVerifyAccountMutation,
    socialLoginMutation,
    setSubmitting,
    setErrors,
    template,
    minimalistic,
    modalMountNode,
    dimmer
  } = _ref4;
  const onSocialLoginSuccess = async (network, user) => {
    try {
      const jsonProfile = JSON.stringify(user.profile);
      // log.trace(jsonProfile);
      // debugger; // eslint-disable-line
      const response = await socialLoginMutation({
        variables: {
          network,
          accessToken: user.token.accessToken,
          rememberMe: values.rememberMe,
          jsonProfile
        }
      });
      handleServerLoginResponse({
        loginData: response.data.socialLogin,
        rememberMe: values.rememberMe,
        onLogin,
        onClose,
        setSubmitting,
        setErrors,
        setValues,
        values,
        fromSocialLogin: true
      });
    } catch (err) {
      setSubmitting(false);
      setErrors({
        socialLogin: [err.message]
      });
    }
  };
  const onLoginFailure = err => {
    setSubmitting(false);
    setErrors(normalizeErrors({
      path: 'socialLogin',
      message: err
    }));
  };
  const onResendVerification = async (e, email, mutation) => {
    mutation({
      variables: {
        email,
        origin: REACT_APP_ENJOY_SITE_ROOT_URL
      }
    });
    e.target.innerText = 'Email Sent';
    e.target.disabled = true;
  };
  const updateValues = updates => setValues({
    ...values,
    ...updates
  });
  return /*#__PURE__*/React.createElement("div", null, template === 'default' && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Header, {
    color: "blue",
    attached: "top"
  }, /*#__PURE__*/React.createElement(EnjoyIcon, {
    className: "formTitleIcon"
  }), " \xA0 Sign in to your account"), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.email
  }, /*#__PURE__*/React.createElement(Input, {
    name: "email",
    id: "email",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.email,
    icon: "user",
    iconPosition: "left",
    placeholder: "Email",
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.password
  }, /*#__PURE__*/React.createElement(Input, {
    name: "password",
    id: "password",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.password,
    type: "password",
    icon: "lock",
    iconPosition: "left",
    placeholder: "Password",
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Button, {
    className: "square",
    type: "submit",
    size: "large",
    primary: true,
    disabled: Boolean(isSubmitting),
    onClick: handleSubmit,
    fluid: true
  }, "Sign In")), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Grid, {
    columns: 2
  }, /*#__PURE__*/React.createElement(Grid.Row, {
    textAlign: "center"
  }, /*#__PURE__*/React.createElement(Grid.Column, null, /*#__PURE__*/React.createElement(Checkbox, {
    className: "square",
    name: "rememberMe",
    id: "rememberMe"
    // onClick={(e) => { e.preventDefault(); e.target.checked = !e.target.checked; }}
    ,
    onChange: (e, data) => {
      setFieldValue(data.id, data.checked);
    }
    // onKeyDown={(e) => { if (!e.repeat && e.keyCode === 32) { e.target.checked = !e.target.checked; } }}
    ,
    label: "Remember Me"
  })), /*#__PURE__*/React.createElement(Grid.Column, null, /*#__PURE__*/React.createElement(ButtonLink, {
    onClick: () => {
      onForgotPassword();
      if (onClose) onClose();
    }
  }, "Forgot Password?")))))), touched.email && errors.email || touched.password && errors.password || errors.login || errors.socialLogin ? /*#__PURE__*/React.createElement(Message, {
    size: "small",
    error: true,
    header: "Trouble logging in...",
    list: _.concat(errors.email, errors.password, errors.login, errors.socialLogin)
  }) : null, errors.notVerified && /*#__PURE__*/React.createElement(Message, {
    error: true,
    size: "small"
  }, /*#__PURE__*/React.createElement(Message.Header, null, "Trouble logging in..."), /*#__PURE__*/React.createElement("div", null, "Account has not yet been verified.\xA0\xA0", /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: e => onResendVerification(e, values.email, resendVerifyAccountMutation)
  }, "Send verification email again?"))), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Segment, null, /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
    style: {
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    disabled: Boolean(isSubmitting),
    style: {
      margin: '0.5em'
    },
    basic: true
    // positive
    ,
    color: "blue",
    fluid: true,
    onClick: () => {
      onNewAccount();
      if (onClose) onClose();
    }
  }, "New? Sign Up Now")), /*#__PURE__*/React.createElement(Divider, {
    horizontal: true
  }, "Or"), /*#__PURE__*/React.createElement(Form.Field, {
    inline: true
  }, /*#__PURE__*/React.createElement(SocialLoginForm, {
    onSocialLoginSuccess: (network, user) => onSocialLoginSuccess(network, user),
    onLoginFailure: onLoginFailure
  }))))), template === 'simple' && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Header, {
    size: "huge",
    color: "black"
  }, "Sign In"), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.email
  }, /*#__PURE__*/React.createElement(Input, {
    placeholder: "Email",
    size: "large",
    name: "email",
    id: "email",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.email,
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.password
  }, /*#__PURE__*/React.createElement(Input, {
    placeholder: "Password",
    size: "large",
    name: "password",
    id: "password",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.password,
    type: "password",
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Button, {
    className: "square",
    type: "submit",
    size: "huge",
    primary: true,
    disabled: Boolean(isSubmitting),
    onClick: handleSubmit,
    fluid: true
  }, "Sign In")), !(touched.email && errors.email || touched.password && errors.password || errors.login || errors.socialLogin) && /*#__PURE__*/React.createElement(Divider, {
    hidden: true,
    fitted: true
  })), (touched.email && errors.email || touched.password && errors.password || errors.login || errors.socialLogin) && /*#__PURE__*/React.createElement(Message, {
    size: "small",
    error: true,
    header: "Trouble logging in...",
    list: _.concat(errors.email, errors.password, errors.login, errors.socialLogin)
  }), errors.notVerified && /*#__PURE__*/React.createElement(Message, {
    error: true,
    size: "small"
  }, /*#__PURE__*/React.createElement(Message.Header, null, "Trouble logging in..."), /*#__PURE__*/React.createElement("div", null, "Account has not yet been verified.\xA0\xA0", /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: e => onResendVerification(e, values.email, resendVerifyAccountMutation)
  }, "Send verification email again?"))), errors.EmailNotFound && /*#__PURE__*/React.createElement(Message, {
    error: true,
    size: "small"
  }, /*#__PURE__*/React.createElement("div", null, "Sorry, we can't find an account with this email address. Please try again or\xA0", !minimalistic && /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: onNewAccount
  }, "create a new account."))), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Grid, {
    columns: 2
  }, /*#__PURE__*/React.createElement(Grid.Row, {
    textAlign: "center"
  }, /*#__PURE__*/React.createElement(Grid.Column, null, /*#__PURE__*/React.createElement(Checkbox, {
    className: "square",
    name: "rememberMe",
    id: "rememberMe"
    // onClick={(e) => { e.preventDefault(); e.target.checked = !e.target.checked; }}
    ,
    onChange: (e, data) => {
      setFieldValue(data.id, data.checked);
    }
    // onKeyDown={(e) => { if (!e.repeat && e.keyCode === 32) { e.target.checked = !e.target.checked; } }}
    ,
    label: "Remember Me"
  })), !minimalistic && /*#__PURE__*/React.createElement(Grid.Column, null, /*#__PURE__*/React.createElement(ButtonLink, {
    onClick: () => {
      onForgotPassword();
      if (onClose) onClose();
    }
  }, "Forgot Password?"))))), /*#__PURE__*/React.createElement(Divider, {
    horizontal: true
  }, "or sign in with"), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(SocialLoginForm, {
    onSocialLoginSuccess: (network, user) => onSocialLoginSuccess(network, user),
    onLoginFailure: onLoginFailure
  })), !minimalistic && /*#__PURE__*/React.createElement(Form.Field, {
    inline: true
  }, "New to Enjoy?", /*#__PURE__*/React.createElement(ButtonLink, {
    size: "large",
    disabled: Boolean(isSubmitting),
    onClick: () => {
      onNewAccount();
      if (onClose) onClose();
    }
  }, "\xA0\xA0Sign up now.")))), template === 'no-social' && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Header, {
    color: "blue",
    attached: "top"
  }, /*#__PURE__*/React.createElement(EnjoyIcon, {
    className: "formTitleIcon"
  }), " \xA0 Sign in to your account"), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.email
  }, /*#__PURE__*/React.createElement(Input, {
    name: "email",
    id: "email",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.email,
    icon: "user",
    iconPosition: "left",
    placeholder: "Email",
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, {
    error: !!errors.password
  }, /*#__PURE__*/React.createElement(Input, {
    name: "password",
    id: "password",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.password,
    type: "password",
    icon: "lock",
    iconPosition: "left",
    placeholder: "Password",
    fluid: true
  })), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Button, {
    className: "square",
    type: "submit",
    size: "large",
    primary: true,
    disabled: Boolean(isSubmitting),
    onClick: handleSubmit,
    fluid: true
  }, "Sign In")), /*#__PURE__*/React.createElement(Form.Field, null, /*#__PURE__*/React.createElement(Grid, {
    columns: 2
  }, /*#__PURE__*/React.createElement(Grid.Row, {
    textAlign: "center"
  }, /*#__PURE__*/React.createElement(Grid.Column, null, /*#__PURE__*/React.createElement(Checkbox, {
    className: "square",
    name: "rememberMe",
    id: "rememberMe"
    // onClick={(e) => { e.preventDefault(); e.target.checked = !e.target.checked; }}
    ,
    onChange: (e, data) => {
      setFieldValue(data.id, data.checked);
    }
    // onKeyDown={(e) => { if (!e.repeat && e.keyCode === 32) { e.target.checked = !e.target.checked; } }}
    ,
    label: "Remember Me"
  })), /*#__PURE__*/React.createElement(Grid.Column, null, /*#__PURE__*/React.createElement(ButtonLink, {
    onClick: () => {
      onForgotPassword();
      if (onClose) onClose();
    }
  }, "Forgot Password?")))))), touched.email && errors.email || touched.password && errors.password || errors.login || errors.socialLogin ? /*#__PURE__*/React.createElement(Message, {
    size: "small",
    error: true,
    header: "Trouble logging in...",
    list: _.concat(errors.email, errors.password, errors.login, errors.socialLogin)
  }) : null, errors.notVerified && /*#__PURE__*/React.createElement(Message, {
    error: true,
    size: "small"
  }, /*#__PURE__*/React.createElement(Message.Header, null, "Trouble logging in..."), /*#__PURE__*/React.createElement("div", null, "Account has not yet been verified.\xA0\xA0", /*#__PURE__*/React.createElement(ButtonLink, {
    color: "blue",
    onClick: e => onResendVerification(e, values.email, resendVerifyAccountMutation)
  }, "Send verification email again?"))), /*#__PURE__*/React.createElement(Divider, {
    hidden: true
  }), /*#__PURE__*/React.createElement(Segment, null, /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Field, {
    style: {
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    disabled: Boolean(isSubmitting),
    style: {
      margin: '0.5em'
    },
    basic: true
    // positive
    ,
    color: "blue",
    fluid: true,
    onClick: () => {
      onNewAccount();
      if (onClose) onClose();
    }
  }, "New? Sign Up Now"))))), /*#__PURE__*/React.createElement(Modal, {
    className: "chooseProfileModal enjoyModal",
    open: Boolean(values.NoActiveProfile),
    size: "tiny",
    mountNode: modalMountNode || undefined,
    dimmer: dimmer || true
  }, /*#__PURE__*/React.createElement(Modal.Header, null, ((values.NoActiveProfile || {}).Profiles || []).length === 1 && (values.NoActiveProfile.Profiles[0] || {}).securedByPIN ? 'Enter your profile PIN' : 'Choose your profile'), /*#__PURE__*/React.createElement(Modal.Content, null, /*#__PURE__*/React.createElement(ChooseProfileForm, {
    user: values.NoActiveProfile || {},
    onSelectProfile: _ref5 => {
      let {
        user,
        profile
      } = _ref5;
      return handleLoginSuccess(user, profile, (values.NoActiveProfile || {}).loginData);
    },
    closeForm: () => {
      updateValues({
        NoActiveProfile: null
      });
      setSubmitting(false);
    },
    mountNode: modalMountNode || undefined
  }))), /*#__PURE__*/React.createElement(EnterPinForm, {
    modalMode: true,
    user: (values.ForceProfilePinPrompt || {}).user || {},
    showEnterPinModal: Boolean(values.ForceProfilePinPrompt),
    profile: (values.ForceProfilePinPrompt || {}).profile || {},
    onSuccessClose: () => {
      const {
        user,
        profile,
        loginData
      } = values.ForceProfilePinPrompt || {};
      handleLoginSuccess(user, profile, loginData);
      updateValues({
        ForceProfilePinPrompt: null
      });
    },
    onFailClose: () => {
      updateValues({
        ForceProfilePinPrompt: null
      });
      setSubmitting(false);
      // There is a problem where the social login buttons don't work again in this case.
      // So I'm taking the easy way out and just reloading the page at this point
      window.location.reload();
    },
    onForgotPinClose: () => {
      updateValues({
        forgotPinModalOpen: true
      });
    },
    mountNode: modalMountNode || undefined,
    dimmer: dimmer || true
  }), /*#__PURE__*/React.createElement(UpdatePinForm, {
    open: values.forgotPinModalOpen,
    onExit: () => {
      updateValues({
        forgotPinModalOpen: false
      });
    },
    user: (values.ForceProfilePinPrompt || {}).user || {},
    forgotPinMode: true,
    mountNode: modalMountNode || undefined,
    dimmer: dimmer || true
  }));
};
const loginMutation = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    mutation($email: String!, $password: String!, $rememberMe: Boolean!) {\n        login(email: $email, password: $password, rememberMe: $rememberMe) {\n            ok\n            userId\n            email\n            token\n            refreshToken\n            selector\n            validator\n            activeProfileId\n            Profiles {\n                id\n                name\n                avatarUrl\n                pin\n                securedByPIN\n            }\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
const socialLoginMutation = gql(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    mutation($network: String!, $accessToken: String!, $rememberMe: Boolean!, $jsonProfile: String!) {\n        socialLogin(network: $network, accessToken: $accessToken, rememberMe: $rememberMe, jsonProfile: $jsonProfile) {\n            ok\n            userId\n            email\n            token\n            refreshToken\n            selector\n            validator\n            activeProfileId\n            Profiles {\n                id\n                name\n                avatarUrl\n                pin\n                securedByPIN\n            }\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
const resendVerifyAccountMutation = gql(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    mutation($email: String!, $origin: String!) {\n        resendVerifyAccount(email: $email, origin: $origin) {\n            ok\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
LoginForm.defaultProps = {
  template: 'default',
  minimalistic: false,
  onClose: () => null,
  modalMountNode: undefined
};
export default compose(graphql(loginMutation, {
  name: 'loginMutation',
  options: {
    client: AuthClient
  }
}), graphql(socialLoginMutation, {
  name: 'socialLoginMutation',
  options: {
    client: AuthClient
  }
}), graphql(resendVerifyAccountMutation, {
  name: 'resendVerifyAccountMutation',
  options: {
    client: AuthClient
  }
}), LoginLogic)(LoginForm);